import { useState, useEffect } from 'react';
import {
  createFileRoute,
  useParams,
  useNavigate,
} from '@tanstack/react-router';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';

import { ProtectedRoute } from '@/components/protected-route';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';

import { selectJobOverviewData, selectSmallWorksByJobId } from '@/store/selectors';
import { addJobState, updateJobState, addSmallWorks, updateSmallWorks } from '@/store/actions';
import { updateHelpModal, updateNotesModal } from '@/store/slices/appStateSlice';
import type { RootState } from '@/store';

export const Route = createFileRoute('/job/$jobId/$date/small-works')({
  component: () => (
    <ProtectedRoute>
      <JobSmallWorksFormPage />
    </ProtectedRoute>
  ),
});

interface FormQuestion {
  question: string;
  id: number;
  value: string | null;
  type?: 'text' | 'radio';
  options?: string[];
}

interface FormSection {
  name: string;
  questions: FormQuestion[];
  note?: string;
}

function JobSmallWorksFormPage() {
  const { jobId, date } = useParams({ from: '/job/$jobId/$date/small-works' });
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Get job data from Redux
  const { booking, job, jobState } = useSelector((state: RootState) =>
    selectJobOverviewData(state, jobId)
  );

  // Get existing small works data
  const smallWorks = useSelector((state: RootState) =>
    selectSmallWorksByJobId(state, jobId)
  );

  const [form, setForm] = useState<FormSection[]>([
    {
      name: 'Covid-19 (Coronavirus)',
      questions: [
        {
          question: "Do you feel you have any symptoms of Covid-19",
          id: 28,
          value: null,
        },
        {
          question: "Do you have the correct PPE",
          id: 29,
          value: null,
        },
        {
          question: "Do you feel you have a safe working environment?",
          id: 30,
          value: null,
        },
      ],
    },
    {
      name: 'Safe System',
      questions: [
        {
          question: "Is the methodology clear",
          id: 1,
          value: null,
        },
        {
          question: "Discuss difficult tasks with Supervisor / Manager",
          id: 2,
          value: null,
        },
        {
          question: "Permits – Confined spaces, Hot works, Electrical",
          id: 3,
          value: null,
        },
        {
          question: "Isolate electrical services",
          id: 4,
          value: null,
        },
      ],
    },
    {
      name: 'Safety & Security',
      questions: [
        {
          question: "Erect Barrier",
          id: 5,
          value: null,
        },
        {
          question: "Do you know emergency procedures / fire exit",
          id: 6,
          value: null,
        },
        {
          question: "Is it Safe to work alone",
          id: 7,
          value: null,
        },
        {
          question: "Check for concealed services",
          id: 8,
          value: null,
        },
      ]
    },
    {
      name: 'Safety Equipment',
      questions: [
        {
          question: "Overalls",
          id: 9,
          value: null,
        },
        {
          question: "Safety Shoes",
          id: 10,
          value: null,
        },
        {
          question: "Eye Protection",
          id: 11,
          value: null,
        },
        {
          question: "Dust Mask",
          id: 12,
          value: null,
        },
        {
          question: "Safety Helmet",
          id: 13,
          value: null,
        },
        {
          question: "Gloves",
          id: 14,
          value: null,
        },
        {
          question: "Hearing Protection",
          id: 15,
          value: null,
        },
        {
          question: "RCD",
          id: 16,
          value: null,
        },
      ]
    },
    {
      name: 'Plant & equipment Required',
      questions: [
        {
          type: 'text',
          question: "Tap here to add",
          id: 17,
          value: null
        },
      ]
    },
    {
      name: 'Health Hazards',
      questions: [
        {
          question: "Check labels on materials and substances.\nIf hazardous, is there a COSH assessment?",
          id: 18,
          value: null,
        },
      ]
    },
    {
      name: 'Excavation',
      note: "All groundworks should only be undertaken by qualified ground workers and the valid permit to dig form completed before starting the works by a manager.",
      questions: [
        {
          question: "Have all underground services been located?",
          id: 19,
          value: null,
        },
        {
          question: "Are shoring materials required?",
          id: 20,
          value: null,
        },
      ]
    },
    {
      name: 'Working at Height',
      note: "A ladder is the last resort and if needed then the ladder stabilisers kit is to be used and that the operative has to be familiarised with its method of use.\nLadders should be used for no more than 30 mins if required.",
      questions: [
        {
          type: 'radio',
          question: "Which is the most appropriate system for access?",
          options: ['Ladder','Access Tower','MEWP','Scaffolding'],
          id: 21,
          value: null
        },
        {
          question: "Has the tower / scaffold, been erected / inspected and certified by a competent person?",
          id: 22,
          value: null,
        },
      ]
    },
    {
      name: 'Manual Handling',
      questions: [
        {
          question: "Is Lifting apparatus required",
          id: 23,
          value: null
        },
      ]
    },
    {
      name: 'Site Tidiness',
      questions: [
        {
          question: "Are Materials stacked and secured",
          id: 24,
          value: null,
        },
        {
          question: "Has all rubbish been removed",
          id: 25,
          value: null,
        },
        {
          question: "Has the site been left in a clean / tidy and safe condition",
          id: 26,
          value: null,
        },
      ]
    },
    {
      name: 'Additional Comments',
      questions: [
        {
          type: 'text',
          question: "Please add additional comments here",
          id: 27,
          value: null,
        },
      ]
    }
  ]);

  // Load existing answers when component mounts
  useEffect(() => {
    if (smallWorks) {
      setForm(prevForm => {
        const updatedForm = [...prevForm];

        // Update each question with existing values
        updatedForm.forEach((section, sectionIdx) => {
          section.questions.forEach((question, questionIdx) => {
            const answerKey = `answer_${question.id}`;
            if (smallWorks[answerKey] !== undefined) {
              let value = smallWorks[answerKey];

              // Convert numeric boolean values to string
              if (value === 1) value = 'yes';
              if (value === 0) value = 'no';

              updatedForm[sectionIdx].questions[questionIdx].value = value;
            }
          });
        });

        return updatedForm;
      });
    }
  }, [smallWorks]);

  const handleGoBack = () => {
    navigate({ to: '/job/$jobId/$date/health-safety', params: { jobId, date } });
  };

  const setAnswer = (sectionIdx: number, questionIdx: number, value: string | null) => {
    setForm(prevForm => {
      const updatedForm = [...prevForm];
      updatedForm[sectionIdx].questions[questionIdx].value = value;
      return updatedForm;
    });
  };

  const handleSubmitForm = () => {
    setConfirmDialogOpen(true);
  };

  const handleCompleteForm = () => {
    if (!job) return;

    // Update job state
    const timestamp = moment().format('YYYY-MM-DD HH:mm:ss');

    if (jobState) {
      dispatch(updateJobState({
        ...jobState,
        completed_small_works: timestamp
      }));
    } else {
      dispatch(addJobState({
        job_id: job.id,
        completed_small_works: timestamp
      }));
    }

    // Collect all answers
    const answers: Record<string, string | null> = {};
    form.forEach((section) => {
      section.questions.forEach((question) => {
        answers[`answer_${question.id}`] = question.value;
      });
    });

    // Save small works data
    if (smallWorks) {
      dispatch(updateSmallWorks({
        ...smallWorks,
        ...answers,
      }));
    } else {
      dispatch(addSmallWorks({
        ...answers,
        job_id: job.id,
      }));
    }

    setConfirmDialogOpen(false);

    // Navigate back to health and safety files
    navigate({ to: '/job/$jobId/$date/health-safety-files', params: { jobId, date } });
  };

  const showNotesModal = () => {
    dispatch(updateNotesModal({
      show: true,
      canAssignTask: true,
    }));
  };

  const showHelpModal = () => {
    dispatch(updateHelpModal(true));
  };

  const renderQuestion = (question: FormQuestion, sectionIdx: number, questionIdx: number) => {
    const key = `question-${sectionIdx}-${questionIdx}`;

    if (question.type === 'text') {
      return (
        <div key={key} className="space-y-2">
          <Label className="text-sm font-medium">{question.question}</Label>
          <Input
            value={question.value || ''}
            onChange={(e) => setAnswer(sectionIdx, questionIdx, e.target.value || null)}
            placeholder="Enter your response..."
            className="w-full"
          />
        </div>
      );
    }

    if (question.type === 'radio' && question.options) {
      return (
        <div key={key} className="space-y-3">
          <Label className="text-sm font-medium">{question.question}</Label>
          <div className="space-y-2">
            {question.options.map((option, optionIdx) => (
              <div key={optionIdx} className="flex items-center space-x-2">
                <input
                  type="radio"
                  id={`${key}-${optionIdx}`}
                  name={`question-${question.id}`}
                  value={option}
                  checked={question.value === option}
                  onChange={() => setAnswer(sectionIdx, questionIdx, option)}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 focus:ring-2"
                />
                <Label htmlFor={`${key}-${optionIdx}`} className="text-sm">
                  {option}
                </Label>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Boolean question (Yes/No)
    return (
      <div key={key} className="space-y-3">
        <Label className="text-sm font-medium whitespace-pre-line">{question.question}</Label>
        <div className="flex space-x-4">
          <div className="flex items-center space-x-2">
            <input
              type="radio"
              id={`${key}-yes`}
              name={`question-${question.id}`}
              value="yes"
              checked={question.value === 'yes'}
              onChange={() => setAnswer(sectionIdx, questionIdx, 'yes')}
              className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 focus:ring-green-500 focus:ring-2"
            />
            <Label htmlFor={`${key}-yes`} className="text-sm text-green-700">
              Yes
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <input
              type="radio"
              id={`${key}-no`}
              name={`question-${question.id}`}
              value="no"
              checked={question.value === 'no'}
              onChange={() => setAnswer(sectionIdx, questionIdx, 'no')}
              className="w-4 h-4 text-red-600 bg-gray-100 border-gray-300 focus:ring-red-500 focus:ring-2"
            />
            <Label htmlFor={`${key}-no`} className="text-sm text-red-700">
              No
            </Label>
          </div>
        </div>
      </div>
    );
  };

  if (!job || !booking) {
    return (
      <div className="col-span-2 flex items-center justify-center h-full">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-600">Job not found</h2>
          <p className="text-gray-500">Could not load job data</p>
        </div>
      </div>
    );
  }

  return (
    <ScrollArea className="col-span-2 h-full overflow-y-auto overflow-x-hidden max-h-[calc(100vh-5rem)]">
      <div className="p-8 flex justify-center">
        <div className="max-w-4xl w-full space-y-8">

          <div className="space-y-4">
            <h1 className="text-3xl font-bold leading-tight m-0">
              Small Works Form
            </h1>
          </div>

          {/* Form Sections */}
          <div className="space-y-8">
            {form.map((section, sectionIdx) => (
              <div key={`section-${sectionIdx}`} className="space-y-4">
                <Label className="text-gray-800 font-semibold text-sm uppercase">
                  {section.name}
                </Label>

                <div className="space-y-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                  {section.questions.map((question, questionIdx) =>
                    renderQuestion(question, sectionIdx, questionIdx)
                  )}

                  {section.note && (
                    <p className="text-xs text-gray-600 mt-2 whitespace-pre-line">
                      {section.note}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="flex gap-4 py-8 border-t border-gray-200">
            <Button
              onClick={handleSubmitForm}
              className="flex-1"
            >
              Submit my Small Works Form
            </Button>

            <Button
              variant="secondary"
              onClick={showHelpModal}
              className="flex-1"
            >
              I Need Assistance
            </Button>
          </div>

          <div className="flex gap-4 pb-8">
            <Button
              variant="outline"
              onClick={handleGoBack}
              className="flex-1"
            >
              Return to Health & Safety
            </Button>

            <Button
              variant="outline"
              onClick={showNotesModal}
              className="flex-1"
            >
              Add Note to Job
            </Button>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Submission</DialogTitle>
            <DialogDescription>
              Do you fully understand the task and associated risks?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2">
            <Button onClick={handleCompleteForm} className="flex-1">
              Yes, I understand
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setConfirmDialogOpen(false);
                showHelpModal();
              }}
              className="flex-1"
            >
              No, I need assistance
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ScrollArea>
  );
}
